import { Component, inject } from '@angular/core';
import {
  compareStringsIgnoreCase,
  DATA_SELECTOR_INJECTION_TOKEN,
  ignoreEmpty,
  isEmpty,
  LoadableStatus,
  removeDuplicatesByField,
} from '@aiwyn/shared';
import { Store } from '@ngxs/store';
import { ClientRequestGroupStatus, CommonClientRequestsState } from '@aiwyn/common/client-requests';
import { ClientRequestsState, UpdateFilter } from '../../state';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import {
  ClientRequestFilters,
  ClientRequestFilterType,
  REQUEST_ITEM_STATUS_FILTER_OPTIONS,
  RequestItemStatusFilterOption,
} from '../../models';
import { BaseClient, BaseJobServiceLine } from '@aiwyn/api';
import { ClientRequestFactoryService } from '../../services';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { ITEM_REQUEST_STATUS_LABEL } from '../../../../utility/status-labels-dictionaries';

interface ClientRequestFilterGroupDisplay {
  type: ClientRequestFilterType;
  label?: string;
  options: {
    id: number | ClientRequestGroupStatus | RequestItemStatusFilterOption;
    label: string;
    matchingIncompleteRequestsCount$: Observable<number>;
  }[];
}

@Component({
  selector: 'app-client-request-filters',
  templateUrl: './client-request-filters.component.html',
  styleUrls: ['./client-request-filters.component.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'client-requests-filters',
    },
  ],
  standalone: false,
})
export class ClientRequestFiltersComponent {
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  services$ = this.store.select(CommonClientRequestsState.getAllServiceLines);
  clients$ = this.store.select(CommonClientRequestsState.getAllClients);

  currentFilterSelections$ = this.store.select(ClientRequestsState.getSlices.filters).pipe(
    map(filters => {
      if (isEmpty(filters)) {
        return {};
      }

      return Object.entries(filters).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: (value as Array<number | ClientRequestGroupStatus>).reduce((acc2, val) => ({ ...acc2, [val]: true }), {}),
        }),
        {},
      );
    }),
    shareReplay(1),
  );

  filterGroups$: Observable<ClientRequestFilterGroupDisplay[]> = combineLatest([
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUESTS_UPDATED_FILTERS),
    this.services$.pipe(
      ignoreEmpty(),
      debounceTime(100),
      map(services => services.sort((a, b) => compareStringsIgnoreCase(a.name, b.name))),
      map(services => ({ type: ClientRequestFilterType.SERVICE, options: services })),
    ),
    this.clients$.pipe(
      ignoreEmpty(),
      debounceTime(100),
      map(clients => clients.sort((a, b) => compareStringsIgnoreCase(a.name, b.name))),
      map(clients => ({ type: ClientRequestFilterType.ACCOUNT, options: clients })),
    ),
  ]).pipe(
    map(([isClientRequestsUpdatedFiltersEnabled, ...filterGroups]) => {
      const itemStatusFilterGroup: ClientRequestFilterGroupDisplay = isClientRequestsUpdatedFiltersEnabled
        ? {
          type: ClientRequestFilterType.ITEM_STATUS,
          label: 'Request Status',
          options: REQUEST_ITEM_STATUS_FILTER_OPTIONS.map(status => ({
            id: status,
            label: ITEM_REQUEST_STATUS_LABEL[status],
            matchingIncompleteRequestsCount$: this._getMatchingIncompleteRequestsCount$({
              [ClientRequestFilterType.ITEM_STATUS]: [status],
            }),
          })),
        }
        : undefined;

      const projectStatusFilterGroup: ClientRequestFilterGroupDisplay = {
        type: ClientRequestFilterType.STATUS,
        label: isClientRequestsUpdatedFiltersEnabled ? 'Project Status' : 'Status',
        options: [ClientRequestGroupStatus.OPEN, ClientRequestGroupStatus.COMPLETED].map(status => ({
          id: status,
          label: status,
          matchingIncompleteRequestsCount$: this._getMatchingIncompleteRequestsCount$({ [ClientRequestFilterType.STATUS]: [status] }),
        })),
      };

      const otherFilterGroups: ClientRequestFilterGroupDisplay[] = filterGroups.map(filterGroup => {
        const { type, options } = filterGroup;
        return {
          type,
          options: removeDuplicatesByField(options, 'id').map((option: BaseJobServiceLine | BaseClient) => ({
            id: option.id,
            label: option.name,
            matchingIncompleteRequestsCount$: this._getMatchingIncompleteRequestsCount$({ [type]: [option.id] }),
          })),
        };
      });

      return isClientRequestsUpdatedFiltersEnabled
        ? otherFilterGroups.concat([itemStatusFilterGroup, projectStatusFilterGroup])
        : [projectStatusFilterGroup, ...otherFilterGroups];
    }),
  );

  readonly LoadableStatus = LoadableStatus;

  constructor(
    private readonly store: Store,
    private readonly clientRequestFactoryService: ClientRequestFactoryService,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {}

  onFilterOptionChange(
    filterType: ClientRequestFilterType,
    optionId: number | ClientRequestGroupStatus | RequestItemStatusFilterOption,
    selected: boolean,
  ): void {
    const existingFilter = this.store.selectSnapshot(ClientRequestsState.getFilter(filterType)) ?? [];
    const resultingFilterValue = selected
      ? existingFilter.concat(optionId)
      : existingFilter.filter(id => id !== optionId);

    this.store.dispatch(new UpdateFilter(filterType, resultingFilterValue));
  }

  private _getMatchingIncompleteRequestsCount$(filters$: ClientRequestFilters): Observable<number> {
    return this.clientRequestFactoryService.getAllFilteredClientRequestGroups$(of(filters$)).pipe(
      map(groups => groups?.length ?? 0),
    );
  }
}
